import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { aborted, blocked, completed, failed, partial } from 'flare-chat-core/contracts/chat/operation-result';
import { normalizeError } from '../../adapters/sources/errors.js';
import { createUploadProgressItem, readFileWithProgress } from './progress.js';
import { hasPendingSourceProcessing, parseSourceList } from '../../adapters/sources/records.js';
import { listProjectSourcesOnce } from './singleflight.js';

const hasPersistentSourceAPI = (api) => ['listProjectSources', 'uploadSourceFile', 'deleteProjectSource']
  .every((key) => typeof api?.[key] === 'function');
const updateProgress = (setter, id, patch) => setter((items) => items.map((item) => item.id === id ? { ...item, ...patch } : item));

/** Project assets are product IO, separate from Core composer attachments.
 * @param {import('./contracts').SourceOwnerOptions} options
 */
export function useProjectSourceUpload({ projectId = '', userId = '', sourceAPI = null, shouldLoadSources = true } = {}) {
  const [sourceItems, setSourceItems] = useState([]);
  const [sourceActionError, setSourceActionError] = useState(null);
  const [sourceUploadLoading, setSourceUploadLoading] = useState(false);
  const [sourceUploadProgressItems, setSourceUploadProgressItems] = useState([]);
  const [sourceSyncLoading, setSourceSyncLoading] = useState(false);
  const [sourceRemovingId, setSourceRemovingId] = useState('');
  const canUsePersistentSourceAPI = hasPersistentSourceAPI(sourceAPI);
  const scope = useMemo(() => ({ active: true, projectId, userId, sourceAPI, uploadPending: false, removePending: false, listSequence: 0 }), [projectId, userId, sourceAPI]);
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const isCurrent = useCallback(() => scope.active && currentScope.current === scope, [scope]);
  useEffect(() => {
    scope.active = true;
    setSourceItems([]); setSourceActionError(null); setSourceUploadProgressItems([]);
    setSourceUploadLoading(false); setSourceSyncLoading(false); setSourceRemovingId('');
    return () => { scope.active = false; };
  }, [scope]);

  const loadProjectSources = useCallback(async ({ silent = false } = {}) => {
    if (!isCurrent() || !canUsePersistentSourceAPI || !projectId) return blocked('project_sources_unavailable');
    const sequence = ++scope.listSequence;
    if (!silent) { setSourceSyncLoading(true); setSourceActionError(null); }
    try {
      const response = await listProjectSourcesOnce(sourceAPI, projectId);
      if (!isCurrent() || sequence !== scope.listSequence) return aborted();
      const items = parseSourceList(response);
      setSourceItems(items);
      return completed(items);
    } catch (error) {
      if (isCurrent() && !silent) setSourceActionError(normalizeError(error, ''));
      return failed(error, 'listProjectSources');
    } finally {
      if (isCurrent() && sequence === scope.listSequence) setSourceSyncLoading(false);
    }
  }, [canUsePersistentSourceAPI, isCurrent, projectId, scope, sourceAPI]);

  useEffect(() => { if (shouldLoadSources) void loadProjectSources(); }, [loadProjectSources, shouldLoadSources]);
  useEffect(() => {
    if (!shouldLoadSources || !canUsePersistentSourceAPI || !hasPendingSourceProcessing(sourceItems)) return undefined;
    const timer = setInterval(() => { void loadProjectSources({ silent: true }); }, 1500);
    return () => clearInterval(timer);
  }, [canUsePersistentSourceAPI, loadProjectSources, shouldLoadSources, sourceItems]);

  const uploadOne = useCallback(async (file, progressItem) => {
    if (!isCurrent()) throw new DOMException('Source scope changed', 'AbortError');
    const progress = (patch) => { if (isCurrent()) updateProgress(setSourceUploadProgressItems, progressItem.id, patch); };
    await readFileWithProgress(file, (percent) => progress({ stage: 'reading', progress: percent }));
    if (!isCurrent()) throw new DOMException('Source scope changed', 'AbortError');
    progress({ stage: 'uploading', progress: 80 });
    const response = await sourceAPI.uploadSourceFile({ project_id: projectId, user_id: userId || null,
      scope: 'project', file, filename: file.name,
      onProgress: (percent) => progress({ stage: 'uploading', progress: 80 + Math.max(0, Math.min(100, Number(percent) || 0)) * 0.2 }),
    });
    progress({ stage: 'done', progress: 100 });
    return response;
  }, [isCurrent, projectId, sourceAPI, userId]);

  const uploadSourceFiles = useCallback(async (/** @type {File[]} */ nativeFiles = []) => {
    if (!isCurrent() || !canUsePersistentSourceAPI || !projectId) return blocked('project_sources_unavailable');
    if (!nativeFiles.length || scope.uploadPending) return blocked('source_upload_empty_or_pending');
    scope.uploadPending = true;
    setSourceUploadLoading(true); setSourceActionError(null);
    const progressItems = nativeFiles.map(createUploadProgressItem);
    const batchIds = new Set(progressItems.map((item) => item.id));
    const acknowledgments = [];
    setSourceUploadProgressItems((items) => [...progressItems, ...items]);
    try {
      await nativeFiles.reduce((previous, file, index) => previous.then(async () => {
        acknowledgments.push(await uploadOne(file, progressItems[index]));
      }), Promise.resolve());
      if (!isCurrent()) return aborted(acknowledgments);
      const refreshed = await loadProjectSources();
      return refreshed.status === 'failed' ? partial(acknowledgments, refreshed.error, 'listProjectSources') : completed(acknowledgments);
    } catch (error) {
      if (!isCurrent()) return aborted(acknowledgments);
      setSourceUploadProgressItems((items) => items.map((item) => !batchIds.has(item.id) || item.stage === 'done'
        ? item : { ...item, stage: 'error', error_message: String(error?.message ?? '') }));
      setSourceActionError(normalizeError(error, ''));
      return acknowledgments.length ? partial(acknowledgments, error, 'uploadSourceFile') : failed(error, 'uploadSourceFile');
    } finally {
      scope.uploadPending = false;
      if (isCurrent()) setSourceUploadLoading(false);
    }
  }, [canUsePersistentSourceAPI, isCurrent, loadProjectSources, projectId, scope, uploadOne]);

  const handleRemoveSource = useCallback(async (sourceId) => {
    if (!isCurrent() || !canUsePersistentSourceAPI || !projectId || !sourceId) return blocked('source_remove_unavailable');
    if (scope.removePending) return blocked('source_remove_pending');
    scope.removePending = true;
    setSourceActionError(null); setSourceRemovingId(sourceId);
    let acknowledgment;
    try {
      acknowledgment = await sourceAPI.deleteProjectSource(projectId, sourceId);
      if (!isCurrent()) return aborted(acknowledgment);
      const refreshed = await loadProjectSources();
      return refreshed.status === 'failed' ? partial({ sourceId, acknowledgment }, refreshed.error, 'listProjectSources') : completed(acknowledgment);
    } catch (error) {
      if (isCurrent()) setSourceActionError(normalizeError(error, ''));
      return failed(error, 'deleteProjectSource');
    } finally {
      scope.removePending = false;
      if (isCurrent()) setSourceRemovingId('');
    }
  }, [canUsePersistentSourceAPI, isCurrent, loadProjectSources, projectId, scope, sourceAPI]);
  const handleRetrySource = useCallback(() => loadProjectSources(), [loadProjectSources]);
  const handleViewSourceDetail = useCallback(() => {}, []);
  const getDomainFields = useCallback((domain) => sourceAPI?.getDomainFields(domain), [sourceAPI]);
  return { sourceCapabilityMode: canUsePersistentSourceAPI ? 'persistent' : 'unavailable',
    sourceActionError, sourceItems, sourceRemovingId, sourceSyncLoading, sourceUploadLoading,
    sourceUploadProgressItems, uploadSourceFiles, handleRemoveSource, handleRetrySource, handleViewSourceDetail, getDomainFields };
}
export default useProjectSourceUpload;
